<template>
  <div
    class="wrapper"
    :class="{
      'wrapper--busy': isInitialized && busy,
    }"
  >
    <Loader
      v-if="!isInitialized && busy"
      label="Данные загружаются"
      :active="busy"
      height="300"
    />
    <div v-show="isInitialized">
      <div class="header">
        <Switcher
          v-if="positions.length > 2"
          v-model="currentPosition"
          class="h-switcher"
          type="group"
          :items="positions"
        />
        <div class="filters">
          <Input
            ref="periodRef"
            v-model="periodText"
            class="filters__period"
            label="Период"
            placeholder="00.00.0000 - 00.00.0000"
            type="period"
            icon="calendar"
            :clearable="true"
            :icon-right="true"
          />
          <Button
            class="filters__apply"
            label="Применить"
            color="primary"
            @click="setPeriod"
          />
        </div>
      </div>
      <ScrolledTable
        class="desktop"
        :fix-last="true"
      >
        <table class="h-table inventory-history inventory-history--desktop">
          <thead>
            <tr>
              <th
                v-for="header in tableHeaders"
                :key="`header_${header.key}`"
                :style="{
                  width: header.width ? `${header.width}px` : null,
                }"
              >
                <TableHeader
                  :header="header"
                  :sort-by.sync="filters.sortBy"
                  :sort-dir.sync="filters.sortDir"
                />
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="entry in entriesByPosition"
              :key="entry.key"
              class="history-item h-table__row"
            >
              <td v-if="positions.length > 2">
                {{ printEntryPositions(entry) || '—' }}
              </td>
              <td>
                <div
                  v-if="entry.item.id"
                  class="name-cell"
                  @click="showEntryHistory(entry)"
                >
                  {{ entry.item.serialNumber }} / {{ entry.item.name || '—' }}
                  <div class="name-cell__type">
                    {{ entry.item.type.name }}
                  </div>
                </div>
                <template v-else>
                  — / —
                </template>
              </td>
              <td>
                {{ entry.item.type.model || '—' }} / {{ entry.item.model || '—' }}
              </td>
              <td>
                {{ entry.item.category && entry.item.category.name || '—' }} / {{ entry.item.subcategory && entry.item.subcategory.name || '—' }}
              </td>
              <td>
                {{ entry.status }}
              </td>
              <td>
                {{ entry.date }}
              </td>
              <td>
                <a
                  v-if="entry.author.worker"
                  :href="`/index.php/?r=redesign#/personal-employee/${entry.author.worker.id}/summary`"
                  target="_blank"
                >
                  {{ entry.author.worker.name }}
                </a>
                <template v-else>
                  {{ entry.author.user.name }}
                </template>
              </td>
              <td>
                <a
                  v-if="entry.item.responsible"
                  :href="`?r=redesign#/personal-employee/${entry.item.responsible.id}/summary`"
                  target="_blank"
                >
                  {{ entry.item.responsible.name }}
                </a>
                <template v-else>
                  —
                </template>
              </td>
              <td>
                <EntryControls
                  :entry="entry"
                  @showComment="showEntryComment"
                  @showInfo="showEntryInfo"
                />
              </td>
            </tr>
          </tbody>
        </table>
      </ScrolledTable>
      <HorizontalTable class="mobile">
        <table class="h-table h-table--horizontal">
          <tr
            v-for="header in tableHeaders"
            :key="header.key"
          >
            <th>
              <TableHeader
                :header="header"
                :sort-by.sync="filters.sortBy"
                :sort-dir.sync="filters.sortDir"
              />
            </th>
            <td
              v-for="entry in entriesByPosition"
              :key="entry.key"
            >
              <EntryControls
                v-if="header.key === 'controls'"
                :entry="entry"
                @showComment="showEntryComment"
                @showInfo="showEntryInfo"
              />
              <template v-else-if="header.key === 'positions'">
                {{ printEntryPositions(entry) || '—' }}
              </template>
              <template v-else-if="header.key === 'guid'">
                <div
                  class="name-cell"
                  @click="showEntryHistory(entry)"
                >
                  {{ entry.item.serialNumber }} / {{ entry.item.name || '—' }}
                  <div class="name-cell__type">
                    {{ entry.item.type.name }}
                  </div>
                </div>
              </template>
              <template v-else-if="header.key === 'model'">
                {{ entry.item.type.model || '—' }} / {{ entry.item.model || '—' }}
              </template>
              <template v-else-if="header.key === 'category'">
                {{ entry.item.category && entry.item.category.name || '—' }} / {{ entry.item.subcategory && entry.item.subcategory.name || '—' }}
              </template>
              <template v-else-if="header.key === 'author'">
                <a
                  v-if="entry.author.worker"
                  :href="`/index.php/?r=redesign#/personal-employee/${entry.author.worker.id}/summary`"
                  target="_blank"
                >
                  {{ entry.author.worker.name }}
                </a>
                <template v-else>
                  {{ entry.author.user.name }}
                </template>
              </template>
              <template v-else-if="header.key === 'responsible'">
                <a
                  v-if="entry.item.responsible"
                  :href="`?r=redesign#/personal-employee/${entry.item.responsible.id}/summary`"
                  target="_blank"
                >
                  {{ entry.item.responsible.name }}
                </a>
                <template v-else>
                  —
                </template>
              </template>
              <template v-else>
                {{ entry[header.key] || "—" }}
              </template>
            </td>
          </tr>
        </table>
      </HorizontalTable>
      <div
        v-if="!entries.length"
        class="empty"
      >
        <template v-if="hasFilters">
          Записи не найдены
        </template>
        <template v-else>
          Изменений пока нет
        </template>
      </div>
    </div>
    <HistorySidesheet ref="historySidesheet" />
    <InfoSidesheet ref="infoSidesheet" />
    <CommentModal ref="commentModal" />
  </div>
</template>

<script>

import api from "@/plugins/api";
import { getRange } from "@/utils/date/ranges";

import Loader from "@/components/loader.vue";
import Input from "@/components/controls2/Input.vue";
import Button from "@/components/controls2/Button.vue";
import Switcher from "@/components/controls2/Switcher.vue";
import ScrolledTable from "@/components/scrolled-table.vue";
import HorizontalTable from "@/components/horizontal-table.vue";
import CommentModal from "@/components/modals/comment-modal/index.vue";
import InfoSidesheet from "@/components/modals/inventory/ItemInfoSidesheet.vue";
import HistorySidesheet from "@/components/modals/inventory/ItemHistorySidesheet.vue";

import EntryControls from "./EntryControls.vue";
import TableHeader from "./TableHeader.vue";

import { History } from "../../models";

export default {
  name: "history-table",
  components: {
    Loader,
    Input,
    Button,
    Switcher,
    ScrolledTable,
    HorizontalTable,
    EntryControls,
    TableHeader,
    CommentModal,
    InfoSidesheet,
    HistorySidesheet,
  },
  props: {
    workerId: {
      type: [String, Number],
      required: true,
    },
  },
  data: () => ({
    periodRef: null,
    busy: true,
    currentPosition: "all",
    filters: {
      position: null,
      periodFrom: null,
      periodTo: null,
      sortBy: "date",
      sortDir: "desc",
    },
    periodText: null,
    isInitialized: false,
    entries: [],
  }),
  computed: {
    tableHeaders () {
      const result = [
        {
          key: "guid",
          text: "Серийный номер / Наименование ТМЦ",
          sortable: true,
        },
        {
          key: "model",
          text: "Модель типа / Модель ТМЦ",
          width: 120,
          sortable: true,
        },
        {
          key: "category",
          text: "Категория / Подкатегория",
          width: 160,
          sortable: true,
        },
        {
          key: "status",
          text: "Статус",
          width: 80,
          sortable: true,
        },
        {
          key: "date",
          text: "Дата изменения",
          width: 100,
          sortable: true,
        },
        {
          key: "author",
          text: "Автор изменения",
          width: 160,
          sortable: true,
        },
        {
          key: "responsible",
          text: "Ответственный",
          width: 140,
          sortable: true,
        },
        {
          key: "controls",
          text: "",
          width: 68,
        },
      ];

      if (this.positions.length > 2) {
        result.unshift({
          key: "positions",
          text: "Должности",
          width: 150,
        });
      }

      return result;
    },
    hasFilters () {
      return Object.entries(this.filters).some(([key, value]) => !String(key).startsWith("sort") && !!value);
    },
    cleanFilters () {
      const result = {};
      const { position, periodFrom, periodTo, sortBy, sortDir } = this.filters;

      if (position) {
        result.post_code = position;
      }

      if (periodFrom && periodTo) {
        result.from = periodFrom;
        result.to = periodTo;
      }

      if (sortBy && ["asc", "desc"].includes(sortDir)) {
        const sortSymbol = sortDir !== "asc" ? "-" : "";

        result.sort = sortSymbol + sortBy;
      }

      return result;
    },
    positions () {
      const result = [
        {
          value: "all",
          label: "Все",
        }
      ];

      let positions = [];

      this.entries.forEach((entry) => {
        positions = [
          ...positions,
          ...entry.positions,
        ];
      });

      (new Set(positions)).forEach((position) => {
        result.push({
          value: position,
          label: position,
        });
      });

      return result;
    },
    entriesByPosition () {
      if (this.currentPosition === "all") {
        return this.entries;
      }

      return this.entries.filter((entry) => {
        if (!entry.positions.includes(this.currentPosition)) {
          return false;
        }

        return true;
      });
    },
  },
  watch: {
    filters: {
      handler () {
        this.fetchData();
      },
      deep: true,
    },
  },
  async mounted() {
    this.periodRef = this.$refs.periodRef;

    this.dequerifyFilters();
    
    await this.fetchData();

    this.isInitialized = true;
  },
  methods: {
    setPeriod () {
      this.filters.periodFrom = this.periodRef.period.start.input;
      this.filters.periodTo = this.periodRef.period.end.input;
    },
    querifyFilters () {
      const queryData = {
        ...(this.$route.query || {}),
      };

      const oldQueryDataJson = JSON.stringify(queryData);

      Object.entries(this.filters).forEach(([key, value]) => {
        value = String(value || "").trim();

        if (value) {
          queryData[`inv_history__${key}`] = value;
        } else {
          delete queryData[`inv_history__${key}`];
        }
      });

      if (oldQueryDataJson === JSON.stringify(queryData)) {
        return;
      }

      this.$router.push({
        name: "personal-employee-inventory-items",
        query: queryData,
      });
    },
    dequerifyFilters () {
      const queryData = this.$route.query || {};

      Object.entries(this.filters).forEach(([key, value]) => {
        value = String(queryData[`inv_history__${key}`] || "").trim();

        if (value) {
          this.filters[key] = value;
        }
      });

      const { periodFrom, periodTo } = this.filters;

      if (!!periodFrom && !!periodTo) {
        this.periodRef.period.start.input = periodFrom;
        this.periodRef.period.end.input = periodTo;
        this.periodRef.dateValue = getRange(periodFrom, periodTo);
      }
    },
    async fetchData() {
      this.busy = true;

      try {
        this.querifyFilters();

        const { data } = await api.get("inventory-worker/history", {
          params: {
            id: this.workerId,
            ...this.cleanFilters,
          },
        });

        this.entries = (new History(data)).entries;
      } catch (error) {
        console.error(error);
      }
      
      this.busy = false;
    },
    printEntryPositions ({ positions = [] }) {
      if (!positions.length) {
        return null;
      }

      return positions.join(", ");
    },
    showEntryHistory({ item }) {
      this.$refs.historySidesheet.open(item);
    },
    showEntryInfo({ item }) {
      this.$refs.infoSidesheet.open(item.id);
    },
    showEntryComment(entry) {
      this.$refs.commentModal.open(entry.comment);
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~@/assets/styles/vars';

.wrapper {
  &--busy {
    opacity: 0.5;
    pointer-events: none;
    cursor: wait;
  }

  @include mobileAndTablet {
    padding-bottom: 20px;
  }
}

.header {
  display: flex;
  align-items: flex-end;
  margin: 20px 0 25px;
  gap: 30px;

  @include onlyMobile {
    flex-direction: column;
    align-items: stretch;
    gap: 20px;
  }
}

.filters {
  display: flex;
  align-items: flex-end;

  &__period {
    min-width: 212px;
  }

  &__apply {
    margin-left: 30px;
  }

  @include onlyMobile {
    flex-direction: column;
    align-items: stretch;

    &__apply {
      margin: 24px 0 0 0;
    }
  }
}

.name-cell {
  color: $color-primary;
  cursor: pointer;

  &__type {
    margin-top: 5px;
    color: $color-label;
    line-height: 110%;
  }
}

.mobile {
  display: none;
  margin: 0 -20px;

  th {
    width: 135px;
    max-width: 135px;
    min-width: 135px;
  }

  td {
    min-width: 190px;
  }
}

@include onlyMobile {
  .mobile {
    display: block;
  }

  .desktop {
    display: none;
  }
}

.empty {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 38px;
  margin-top: -14px;
  margin-right: 1px;
  border: 1px solid #DFE4EF;
  border-right-width: 2px;
  text-align: center;
  font-size: 13px;
  color: $color-label;
  background: white;
  z-index: 2;
}

</style>
